import { Divider } from '@byecode/ui'
import { BACKGROUND_TYPE } from '@lighthouse/core'
import { getImageFullUrlInApplication, Group4ByecodeUi, IconPicker, ListItem4ByecodeUi } from '@lighthouse/shared'
import React, { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { AnchorSetting } from '@/components/AnchorSetting'
import { EventList } from '@/components/EventList'
import { InheritLabel } from '@/components/InheritLabel'
import { getBlockAndPageDesignLimit, PositionSetting } from '@/components/StyleSetting'
import AnimationSetting from '@/components/StyleSetting/AnimationSetting'
import { FillColor } from '@/components/StyleSetting/components/FillColor'
import { useSetExtendsKeys } from '@/components/StyleSetting/hooks/useMouseEventDistance'
import { SizeSetting } from '@/components/StyleSetting/SizeSetting'
import { VisibilitySetting } from '@/components/VisibilitySetting'
import { useBlockSettingContext } from '@/contexts/BlockSettingContext'
import { useCurrentAppId } from '@/hooks/useApplication'
import { usePageList } from '@/hooks/usePage'
import { uploadManagerInAppParams } from '@/utils/auth'

import { BlockConfigureSegmentedControl } from '../Common/BlockConfigureSegmentedControl'
import { CursorSetting } from '../Common/CursorSetting'
import { FloatingControl } from '../Common/FloatingControl'
import type { BlockSettingsSegmentedEnum } from '../constants'
import { BLOCK_SETTINGS_SEGMENTED_ENUM } from '../constants'

interface IconSettingProps {}

export const IconSetting: React.FC<IconSettingProps> = () => {
    const { control, watch } = useFormContext /* <IconBlockConfig> */()
    const [currentTab, setCurrentTab] = useState<BlockSettingsSegmentedEnum>(BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN)
    const styleProps = getBlockAndPageDesignLimit('icon')
    const appId = useCurrentAppId()
    const { blockId } = useBlockSettingContext()

    const [action] = watch(['action'])
    const { handleSetBreakKey } = useSetExtendsKeys()
    const allPages = usePageList()

    return (
        <>
            <BlockConfigureSegmentedControl value={currentTab} onChange={setCurrentTab} />
            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN && (
                <>
                    <PositionSetting {...styleProps.position} />
                    <Divider />
                    <SizeSetting {...styleProps.size} />
                    <Divider />
                    <Group4ByecodeUi label="图标">
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <InheritLabel label="选择图标" name="breakPoint.icon" />
                            <Controller
                                control={control}
                                name="breakPoint.icon"
                                render={({ field }) => (
                                    <IconPicker
                                        value={field.value}
                                        uploadParams={uploadManagerInAppParams()}
                                        onGetFullUrl={url => getImageFullUrlInApplication(appId, url)}
                                        onChange={v => {
                                            handleSetBreakKey('breakPoint.icon')
                                            field.onChange(v)
                                        }}
                                    />
                                )}
                            />
                        </ListItem4ByecodeUi>
                    </Group4ByecodeUi>
                    <Divider />
                    <Group4ByecodeUi label="样式">
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <InheritLabel label="图标颜色" name="breakPoint.design.color" />
                            <Controller
                                control={control}
                                name="breakPoint.design.color"
                                render={({ field }) => {
                                    return (
                                        <FillColor
                                            label="图标颜色"
                                            value={field.value}
                                            onChange={val => {
                                                field.onChange(val)
                                                handleSetBreakKey('breakPoint.design.color')
                                            }}
                                            enabledBackgroundTypes={[BACKGROUND_TYPE.color, BACKGROUND_TYPE.variable]}
                                        />
                                    )
                                }}
                            />
                        </ListItem4ByecodeUi>
                    </Group4ByecodeUi>
                    <Divider />
                    <Controller name="cursor" render={({ field }) => <CursorSetting {...field} />} />
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION && (
                <>
                    <Controller
                        name="eventList"
                        control={control}
                        render={({ field }) => <EventList blockType="icon" value={field.value} onChange={field.onChange} />}
                    />

                    <Divider />
                    <Controller
                        name="floatPointerList"
                        control={control}
                        render={({ field }) => {
                            return <FloatingControl value={field.value} blockId={blockId} onChange={field.onChange} />
                        }}
                    />
                    <Divider />
                    <AnimationSetting />
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE && (
                <>
                    <AnchorSetting />
                    <Divider />
                    <VisibilitySetting />
                </>
            )}
        </>
    )
}
